// MissionDisplay component: Renders the list of missions, pagination, and the mission detail view.
import {
  Building,
  Calendar,
  ExternalLink,
  LinkIcon,
  Plus,
  ReferenceIcon,
  Signer,
} from 'assets/icons';
import BadgeSquare from 'components/badge/badgeSquare';
import Button from 'components/button';
import CopyToClipboard from 'components/copyToClipboard';
import EmptyState from 'components/EmptyState/EmptyState';
import Pagination from 'components/pagination/pagination';
import StyledInput from 'components/styledInput/StyledInput';
import TempWorkerMission from 'components/tempWorkerMission/tempWorkerMission';
import Tooltip from 'components/tooltips/Tooltips';
import MediaQuery from 'react-responsive';
import ReactDOM from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import useSlideOver from 'components/SlideOver/useSlideOver';
import UseContext from 'hooks/useContext';
import { dateWordShort, generateEmployeeColor } from 'helper/helper';
import AdminMissionDetails from 'pages/pages/mission/adminMissionDetails';
import ApiDatabase from 'server';
import { buildMatchPresentation, highlightSlot } from 'utils/searchResultHighlight';

const MissionDisplay = ({
  searchMissionResult,
  search = '',
  editMission,
  setEditMission,
  selectedMissions,
  employees,
  hoveredCompanyItems,
  setHoveredCompanyItems,
  openCompanyInfo,
  openTempWorkerInfo,
  handleSelectMission,
  updateEmployeeForMission,
  currentPage,
  countPerPage,
  countTotal,
  totalPages,
  setCurrentPage,
  setSearchMissionResult,
}) => {
  const { toggleSlideOver } = useSlideOver();
  const { setObjID } = UseContext();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const dropdownRefs = useRef({});
  const triggerRefs = useRef({});

  // Calculer les positions des dropdowns ouverts
  useEffect(() => {
    Object.keys(openDropdowns).forEach((missionId) => {
      if (openDropdowns[missionId] && triggerRefs.current[missionId]) {
        const rect = triggerRefs.current[missionId].getBoundingClientRect();
        setDropdownPositions((prev) => ({
          ...prev,
          [missionId]: {
            top: rect.bottom + 4,
            left: rect.left
          }
        }));
      }
    });
  }, [openDropdowns]);

  // Fermer les dropdowns quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(openDropdowns).forEach((missionId) => {
        if (openDropdowns[missionId]) {
          const triggerRef = triggerRefs.current[missionId];
          const dropdownRef = dropdownRefs.current[missionId];
          if (
            triggerRef &&
            !triggerRef.contains(event.target) &&
            dropdownRef &&
            !dropdownRef.contains(event.target)
          ) {
            setOpenDropdowns((prev) => ({ ...prev, [missionId]: false }));
          }
        }
      });
    };

    if (Object.values(openDropdowns).some((isOpen) => isOpen)) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);

  const toggleDropdown = (missionId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [missionId]: !prev[missionId],
    }));
  };

  const handleMissionClick = (missionId) => {
    const token = localStorage.getItem('xsrfToken');
    ApiDatabase.postLogMissionClick(
      { token, idMission: missionId },
      () => {},
      (err) => console.error('Log mission click error:', err)
    );

    if (window.innerWidth < 1200) {
      setObjID(missionId);
      toggleSlideOver('get_mission');
    } else {
      setEditMission(missionId);
    }
  };

  return (
    <>
      {/* Left side: Mission list and pagination */}
      <div className="flex-1 h-full w-full lg:w-[555px] mission-container-body-list flex flex-col">
        <div className="flex flex-col gap-3 overflow-y-auto flex-1">
            {[...searchMissionResult].sort((a, b) => {
              const inactiveStatuses = ['suspended', 'canceled', 'interrupted', 'deleted'];
              const aUrgent = a.is_urgent && !inactiveStatuses.includes(a.status) ? 1 : 0;
              const bUrgent = b.is_urgent && !inactiveStatuses.includes(b.status) ? 1 : 0;
              return bUrgent - aUrgent;
            }).map((mission) => {
              const missionKey =
                mission.id_mission_real?.toString?.() || mission._id;
              const companyHoverKey = `${missionKey}-${mission.company?.id_company}`;
              const match = buildMatchPresentation({
                search,
                item: mission,
                context: 'missionList',
              });
              const companyLabel = mission.company?.reason_social === mission.company?.name_commercial
                ? mission.company?.reason_social
                : `${mission.company?.reason_social} => ${mission.company?.name_commercial}`;
              const companyText = `${companyLabel || ''}${mission.company?.id_bestt ? ` n°${mission.company.id_bestt}` : ''}`;
              const missionIdLabel = `n°${mission.id_mission}`;
              const contractLabel = mission.contract_tempworker?.[0]?.id_bestt
                ? `Contrat n°${mission.contract_tempworker[0].id_bestt}`
                : 'Aucun contrat BeSTT';
              const referenceLabel = mission.reference ? `Référence n°${mission.reference}` : null;

              if (
                process.env.NODE_ENV === 'development' &&
                search?.trim().length >= 2 &&
                match.matchHints.length > 0
              ) {
                console.log(
                  `[searchHighlight][MissionDisplay] render Trouvé lines\n${JSON.stringify(
                    {
                      search,
                      missionId: mission.id_mission,
                      matchHints: match.matchHints,
                    },
                    null,
                    2
                  )}`
                );
              }

              return (
              <div
                key={missionKey}
                className={`flex flex-col gap-1.5 ${
                  (editMission?.toString?.() || editMission) ===
                  (mission.id_mission_real?.toString?.() || mission.id_mission_real)
                    ? 'border border-yellow-500'
                    : 'border border-gray-100'
                } rounded-lg p-4 cursor-pointer hover:bg-gray-50 group mission-container-body-list-item`}
                // Select the mission when clicked
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMissionClick(mission.id_mission_real);
                }}
              >
                <div className="flex gap-1.5 justify-between items-center">
                  {/* Checkbox for bulk mission selection; visible on hover */}
                  <div
                    className={`${
                      selectedMissions.includes(mission.id_mission_real)
                        ? 'block'
                        : 'hidden group-hover:block'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <StyledInput
                      type="checkbox"
                      checked={selectedMissions.includes(
                        mission.id_mission_real
                      )}
                      onChange={(e) =>
                        handleSelectMission(
                          mission.id_mission_real,
                          e.target.checked
                        )
                      }
                    />
                  </div>
                  {/* Mission title and ID */}
                  <div className="flex items-center gap-1 flex-1 min-w-0 [&_strong]:font-bold [&_strong]:bg-yellow-50">
                    <div className="truncate">{mission.title}</div>
                    <div>{highlightSlot(missionIdLabel, match.slotTerms, 'missionId')}</div>
                  </div>
                  {/* Action buttons and employee assignment display */}
                  <div className="flex gap-1.5 items-center text-xs mission-container-body-list-item-actions">
                    {!['suspended', 'canceled', 'interrupted', 'deleted'].includes(mission.status) && mission.is_urgent && (
                      <BadgeSquare type={11} size="12" />
                    )}
                    {mission.status === 'suspended' && (
                      <BadgeSquare type={22} size="12" />
                    )}
                    {mission.status === 'canceled' && (
                      <BadgeSquare type={23} size="12" />
                    )}
                    {mission.status === 'interrupted' && (
                      <BadgeSquare type={24} size="12" />
                    )}
                    {mission.status === 'deleted' && (
                      <BadgeSquare type={25} size="12" />
                    )}
                    <div className="hidden group-hover:flex gap-1.5 items-center">
                      {/* Button to copy the mission URL to clipboard */}
                      <CopyToClipboard
                        text={`${process.env.REACT_APP_INTERIM}ficheMissionSearch/${mission.id_mission_real}`}
                        tooltipText="Copier le lien"
                      >
                        <Button size="XS" color="DEFAULT" className="w-[24px] h-[24px] rounded border border-transparent hover:shadow-none hover:border-gray-100 p-0 flex items-center justify-center">
                          <LinkIcon wh={16} color="#374151" />
                        </Button>
                      </CopyToClipboard>
                    </div>
                    <div className="border rounded min-w-[24px] px-1.5 h-[24px] flex items-center justify-center text-xs">
                      {mission.users
                        ? mission.users.filter((user) => user.refused === false).length
                        : 0}
                    </div>
                    {/* Employee assignment dropdown */}
                    {(() => {
                      const missionId = mission.id_mission_real;
                      const isOpen = openDropdowns[missionId] || false;
                      const isAssigned = Boolean(
                        mission.admin_employee_assigned
                      );
                      const currentEmployee = isAssigned
                        ? employees.find(
                            (emp) =>
                              emp._id === mission.admin_employee_assigned
                          )
                        : null;
                      const position = dropdownPositions[missionId] || { top: 0, left: 0 };
                      
                      return (
                        <Tooltip text="Assigner" placement="top" disabled={isOpen}>
                          <div className="relative">
                            <>
                              <div
                                ref={(el) => (triggerRefs.current[missionId] = el)}
                                className="cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleDropdown(missionId);
                                }}
                              >
                                {isAssigned && currentEmployee ? (
                                  <div
                                    className="rounded-full w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white"
                                    style={{
                                      backgroundColor: generateEmployeeColor(
                                        currentEmployee.firstname,
                                        currentEmployee.lastname
                                      ),
                                    }}
                                  >
                                    {currentEmployee.firstname
                                      ?.charAt(0)
                                      .toUpperCase()}
                                    {currentEmployee.lastname
                                      ?.charAt(0)
                                      .toUpperCase()}
                                  </div>
                                ) : (
                                  <div className="outline-dashed outline-gray-300 rounded-full w-[24px] h-[24px] flex items-center justify-center">
                                    <Plus wh={16} color="#374151" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Dropdown menu */}
                              {isOpen && ReactDOM.createPortal(
                                <div
                                  ref={(el) => (dropdownRefs.current[missionId] = el)}
                                  className="fixed z-[99999] bg-white rounded-lg shadow-lg border border-gray-100 py-1 w-48 max-h-64 overflow-y-auto"
                                  style={{ top: position.top, left: position.left }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* Option pour désassigner */}
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const employeeList = [
                                        { value: 'unassign', checked: true },
                                        ...employees.map((emp) => ({
                                          value: emp._id,
                                          checked: false,
                                        })),
                                      ];
                                      updateEmployeeForMission(mission.id_mission_real, employeeList);
                                      setOpenDropdowns((prev) => ({ ...prev, [missionId]: false }));
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2 transition-colors ${
                                      !isAssigned ? 'bg-gray-50' : ''
                                    }`}
                                  >
                                    <div className="outline-dashed outline-gray-300 rounded-full w-[24px] h-[24px] flex items-center justify-center shrink-0">
                                      <Plus wh={16} color="#374151" />
                                    </div>
                                    <span>Non assigné</span>
                                  </button>
                                  
                                  {/* Liste des employés */}
                                  {employees.map((employee) => {
                                    const isSelected = isAssigned && employee._id === mission.admin_employee_assigned;
                                    return (
                                      <button
                                        key={employee._id}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const employeeList = [
                                            { value: 'unassign', checked: false },
                                            ...employees.map((emp) => ({
                                              value: emp._id,
                                              checked: emp._id === employee._id,
                                            })),
                                          ];
                                          updateEmployeeForMission(mission.id_mission_real, employeeList);
                                          setOpenDropdowns((prev) => ({ ...prev, [missionId]: false }));
                                        }}
                                        className={`w-full px-4 py-2 text-left text-sm text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2 transition-colors ${
                                          isSelected ? 'bg-gray-50' : ''
                                        }`}
                                      >
                                        <div
                                          className="rounded-full w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white shrink-0"
                                          style={{
                                            backgroundColor: generateEmployeeColor(
                                              employee.firstname,
                                              employee.lastname
                                            ),
                                          }}
                                        >
                                          {`${
                                            employee.firstname
                                              ?.charAt(0)
                                              .toUpperCase() || ''
                                          }${
                                            employee.lastname
                                              ?.charAt(0)
                                              .toUpperCase() || ''
                                          }`}
                                        </div>
                                        <span>{employee.firstname} {employee.lastname}</span>
                                      </button>
                                    );
                                  })}
                                </div>,
                                document.body
                              )}
                            </>
                          </div>
                        </Tooltip>
                      );
                    })()}
                  </div>
                </div>
                {/* Mission details: Date range, company info, contract, and reference */}
                <div className="text-sm flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-0.5 text-gray-700 w-1/2 [&_strong]:text-gray-900 [&_strong]:font-bold [&_strong]:bg-yellow-50">
                    <div className="flex gap-1 items-center">
                      <div>
                        <Calendar wh={18} color="#374151" />
                      </div>
                      {dateWordShort(mission.duration.datestart) +
                        ' > ' +
                        dateWordShort(mission.duration.dateend)}
                    </div>
                    <div className="flex gap-1 items-center">
                      <div>
                        <Building wh={18} color="#374151" />
                      </div>
                      <div
                        className="flex gap-1"
                        onMouseEnter={() =>
                          setHoveredCompanyItems((prev) => ({
                            ...prev,
                            [companyHoverKey]: true,
                          }))
                        }
                        onMouseLeave={() =>
                          setHoveredCompanyItems((prev) => ({
                            ...prev,
                            [companyHoverKey]: false,
                          }))
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openCompanyInfo(mission.company?.id_company);
                        }}
                      >
                        {highlightSlot(companyText, match.slotTerms, 'company')}
                        {hoveredCompanyItems[companyHoverKey] && (
                          <div>
                            <ExternalLink wh={18} color="#235FE6" />
                          </div>
                        )}
                      </div>
                    </div>
                    {mission.user && (
                      <div className="flex gap-1 items-center">
                        <div>
                          <Signer wh={18} color="#374151" />
                        </div>
                        <div className="flex flex-col">
                          {highlightSlot(contractLabel, match.slotTerms, 'contract')}
                          {mission.contract_tempworker
                            ?.filter((c) => (c.avenant_number || 0) > 0)
                            .map((c) => (
                              <span key={c.avenant_number} className="text-gray-500 text-xs">
                                Avenant n°{c.id_bestt || c.avenant_number}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                    {referenceLabel && (
                      <div className="flex gap-1 items-center">
                        <div>
                          <ReferenceIcon wh={18} color="#374151" />
                        </div>
                        {highlightSlot(referenceLabel, match.slotTerms, 'reference')}
                      </div>
                    )}
                    {match.matchHints.map((hint) => (
                      <div key={hint} className="text-xs italic text-gray-500">
                        {hint}
                      </div>
                    ))}
                  </div>
                  {/* Display temporary worker details if available */}
                  {mission.user && (
                    <div
                      className="w-1/2 rounded-lg border border-gray-100 px-4 py-2.5 text-gray-500 hover:bg-gray-100"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openTempWorkerInfo(mission.user.user_id);
                      }}
                    >
                      <TempWorkerMission user={mission.user} slotTerms={match.slotTerms} />
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
          {/* Pagination component for navigating through mission pages */}
        {currentPage && setCurrentPage && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-8 py-4">
          <Pagination
            currentPage={currentPage}
            countPerPage={countPerPage}
            countTotal={countTotal}
              totalPages={totalPages || 1}
            setCurrentPage={setCurrentPage}
          />
        </div>
        )}
      </div>
      {/* Right side: Detailed view of the selected mission - Hidden under 1200px */}
      <MediaQuery minWidth={1200}>
      <div className="flex-1 overflow-hidden mission-container-body-detail">
        {editMission ? (
          <AdminMissionDetails
            missionId={editMission}
            searchMissionResult={searchMissionResult}
            setSearchMissionResult={setSearchMissionResult}
          />
        ) : (
          <EmptyState type="select_mission" />
        )}
      </div>
      </MediaQuery>
    </>
  );
};

export default MissionDisplay;
